import React, { useState } from "react";
import { RefreshCw } from "lucide-react";
import usePaymentStatsByOperator from "../../../hooks/payments/usePaymentStatsByOperator";
import type { DailyByOperatorStats } from "../../../services/paymentsService";

export interface TransactionByOperatorStatViewProps {
  senderCountryId?: number;
  receiverCountryId?: number;
  corridorLabel?: string;
}

const TransactionByOperatorStatView: React.FC<TransactionByOperatorStatViewProps> = ({
  senderCountryId,
  receiverCountryId,
  corridorLabel,
}) => {
  const [operatorDateFrom, setOperatorDateFrom] = useState<string>("");
  const [operatorDateTo, setOperatorDateTo] = useState<string>("");
  const [operatorOnlySucceeded, setOperatorOnlySucceeded] = useState<boolean>(true);
  const [appliedFilters, setAppliedFilters] = useState<{
    dateFrom?: string;
    dateTo?: string;
    onlySucceeded?: boolean;
  }>({ onlySucceeded: true });

  const { data, loading, error, refetch } = usePaymentStatsByOperator({
    dateFrom: appliedFilters.dateFrom,
    dateTo: appliedFilters.dateTo,
    senderCountryId,
    receiverCountryId,
    onlySucceeded: appliedFilters.onlySucceeded,
    enabled: true,
  });

  const handleApply = () => {
    setAppliedFilters({
      dateFrom: operatorDateFrom || undefined,
      dateTo: operatorDateTo || undefined,
      onlySucceeded: operatorOnlySucceeded,
    });
  };

  const handleReset = () => {
    setOperatorDateFrom("");
    setOperatorDateTo("");
    setOperatorOnlySucceeded(true);
    setAppliedFilters({ onlySucceeded: true });
  };

  return (
    <div className="mb-6">
      <h3 className="mb-3 text-lg font-semibold text-gray-700">
        {corridorLabel
          ? `Statistiques journalières par opérateur — ${corridorLabel}`
          : "Statistiques journalières par opérateur"}
      </h3>

      <div className="flex flex-wrap items-end gap-3 p-4 mb-4 bg-white rounded-lg border border-gray-200">
        <div className="flex flex-col">
          <label className="mb-1 text-xs font-medium text-gray-600">Du</label>
          <input
            type="date"
            value={operatorDateFrom}
            onChange={(e) => setOperatorDateFrom(e.target.value)}
            className="px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500"
          />
        </div>

        <div className="flex flex-col">
          <label className="mb-1 text-xs font-medium text-gray-600">Au</label>
          <input
            type="date"
            value={operatorDateTo}
            onChange={(e) => setOperatorDateTo(e.target.value)}
            className="px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500"
          />
        </div>

        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={operatorOnlySucceeded}
            onChange={(e) => setOperatorOnlySucceeded(e.target.checked)}
            className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500"
          />
          Seulement les paiements réussis
        </label>

        <button onClick={handleApply} className="px-4 py-2 text-white bg-indigo-600 rounded-lg hover:bg-indigo-700">
          Appliquer
        </button>

        <button onClick={handleReset} className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">
          Réinitialiser
        </button>

        <button
          onClick={() => refetch()}
          disabled={loading}
          className="flex gap-2 items-center px-4 py-2 ml-auto text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          Rafraîchir
        </button>
      </div>

      {loading ? (
        <p className="py-6 text-center text-gray-500">Chargement…</p>
      ) : error ? (
        <p className="py-6 text-center text-red-600">{error}</p>
      ) : data.length === 0 ? (
        <p className="py-6 text-center text-gray-500">Aucune donnée disponible.</p>
      ) : (
        <div className="overflow-x-auto bg-white rounded-lg border border-gray-200">
          <table className="w-full text-sm">
            <thead className="text-xs text-gray-600 uppercase bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left">Date</th>
                <th className="px-4 py-3 text-left">Opérateur</th>
                <th className="px-4 py-3 text-right">Transactions</th>
                <th className="px-4 py-3 text-right">Total CAD</th>
                <th className="px-4 py-3 text-right">Total FCFA</th>
              </tr>
            </thead>
            <tbody>
              {data.map((row: DailyByOperatorStats, i: number) => (
                <tr key={`${row.date}-${row.operator}-${i}`} className="border-t border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-2">{row.date}</td>
                  <td className="px-4 py-2 font-medium">{row.operator}</td>
                  <td className="px-4 py-2 text-right">{row.transaction_count}</td>
                  <td className="px-4 py-2 text-right">
                    {row.total_cad.toLocaleString("fr-FR", { minimumFractionDigits: 2 })}
                  </td>
                  <td className="px-4 py-2 text-right">
                    {row.total_fcfa.toLocaleString("fr-FR", { maximumFractionDigits: 0 })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default TransactionByOperatorStatView;
